<template>
  <div class="aba-discount-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Department-wise Discount Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">
        Loading department-wise discount records...
      </p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text"
                >Department-wise Discount Report</span
              >
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <div class="filter-info-line mt-1" v-if="filterSummary">
              {{ filterSummary }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="!departmentGroups.length" class="text-center py-5 no-data">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No records found for the selected criteria</p>
      </div>

      <div
        v-for="(group, gIndex) in departmentGroups"
        :key="`${group.departmentID}-${gIndex}`"
        class="department-box mb-4"
      >
        <div class="department-box-header">
          <h5 class="department-title mb-0">{{ group.departmentName }}</h5>
        </div>

        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center">#</th>
                <th class="text-center">Roll</th>
                <th class="text-left">Student Name</th>
                <th class="text-center">Class</th>
                <th class="text-right">Actual Fee</th>
                <th class="text-right">Discount</th>
                <th class="text-right">Net Payable</th>
                <th class="text-center">Disc %</th>
                <th class="text-center">Category</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in group.students" :key="index">
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-center roll-cell">{{ item.roll || "-" }}</td>
                <td class="text-left font-weight-bold">
                  {{ (item.name || "-").toUpperCase() }}
                </td>
                <td class="text-center">{{ item.className || "-" }}</td>
                <td class="text-right">{{ formatAmount(item.actualFee) }}</td>
                <td class="text-right">
                  {{ formatAmount(item.discountAmount) }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatAmount(item.netPayableFee) }}
                </td>
                <td class="text-center">
                  {{ formatPercent(item.discountPercentage) }}
                </td>
                <td class="text-center">{{ item.category || "-" }}</td>
              </tr>
              <tr class="totals-row">
                <td colspan="4" class="text-right font-weight-bold">
                  {{ group.departmentName }} Totals
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatAmount(group.totalActualFee) }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatAmount(group.totalDiscount) }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatAmount(group.totalNetPayable) }}
                </td>
                <td class="text-center font-weight-bold">
                  {{ formatPercent(group.discountPercent) }}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "DepartmentWiseDiscountPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      classID: 0,
      sectionID: 0,
      departmentID: 0,
      className: "",
      sectionName: "",
      departmentName: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    departmentGroups() {
      const groups = new Map();

      this.reportItems.forEach((item) => {
        const deptId = item.departmentID || 0;
        const deptName = (item.departmentName || "").trim() || "Unassigned";
        const key = `${deptId}-${deptName}`;

        if (!groups.has(key)) {
          groups.set(key, {
            departmentID: deptId,
            departmentName: deptName,
            students: [],
          });
        }
        groups.get(key).students.push(item);
      });

      return Array.from(groups.values())
        .map((group) => {
          const totalActualFee = group.students.reduce(
            (sum, item) => sum + (Number(item.actualFee) || 0),
            0
          );
          const totalDiscount = group.students.reduce(
            (sum, item) => sum + (Number(item.discountAmount) || 0),
            0
          );
          const totalNetPayable = Math.max(0, totalActualFee - totalDiscount);

          return {
            ...group,
            totalActualFee,
            totalDiscount,
            totalNetPayable,
            discountPercent:
              totalActualFee > 0 ? (totalDiscount / totalActualFee) * 100 : 0,
          };
        })
        .sort((a, b) => a.departmentName.localeCompare(b.departmentName));
    },
    filterSummary() {
      const parts = [];
      parts.push(this.className ? `Class: ${this.className}` : "Class: All");
      parts.push(
        this.sectionName ? `Section: ${this.sectionName}` : "Section: All"
      );
      parts.push(
        this.departmentName
          ? `Department: ${this.departmentName}`
          : this.departmentID
          ? `Department ID: ${this.departmentID}`
          : "Department: All"
      );
      return parts.join(" | ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      if (this.reportDomain !== "myskoolaba") {
        this.error = "This report is only available for Myskool ABA.";
        return;
      }
      const q = this.$route.query;
      this.db = q.db || this.$store.state.userData.db;
      this.cID = Number(q.cID || this.$store.state.userData.cId || 0);
      this.classID = Number(q.classID || 0);
      this.sectionID = Number(q.sectionID || 0);
      this.departmentID = Number(q.departmentID || 0);
      this.className = q.className || "";
      this.sectionName = q.sectionName || "";
      this.departmentName = q.departmentName || "";
      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }
      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branch =
          (this.$store.state.branches || []).find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (branch) {
          this.campusName =
            branch.organizationName || branch.branch || "MY SKOOL";
          this.campusBranch = branch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${this.$store.state.domain}Fees/DepartmentWiseDiscountReport`,
          {
            params: {
              db: this.db,
              cID: this.cID,
              classID: this.classID || 0,
              sectionID: this.sectionID || 0,
              departmentID: this.departmentID || 0,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        if (response.data?.status === "success") {
          const raw = response.data.data || [];
          this.reportItems = raw.map((item) => this.normalizeReportItem(item));
        } else {
          this.error =
            response.data?.message || "Unexpected response from server.";
        }
      } catch (err) {
        console.error(err);
        this.error = "Failed to retrieve report data.";
      } finally {
        this.loading = false;
      }
    },
    normalizeReportItem(item) {
      const actualFee = Number(item.actualFee ?? item.classFee) || 0;
      const discountAmount = Number(item.discountAmount ?? item.discount) || 0;
      const netPayableFee = Math.max(0, actualFee - discountAmount);
      const discountPercentage =
        actualFee > 0 ? (discountAmount / actualFee) * 100 : 0;

      return {
        ...item,
        actualFee,
        discountAmount,
        netPayableFee,
        discountPercentage,
      };
    },
    formatAmount(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "-";
      return num.toLocaleString("en-PK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatPercent(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "-";
      return `${num.toFixed(2)}%`;
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped src="./abaDiscountReportPrint.scss"></style>
